import { CalendarDay, CalendarWeek, useDayPicker, WeeksProps } from "react-day-picker";
import { useCallback, useContext, useMemo, useState } from "react";

import CustomTooltip, { StyleToolTip } from "./CustomTooltip";
import {
	canDisplayWeek,
	compareTwoDate,
	DayPickerContext,
	getShiftNameByNumber,
	isCurrentWeek,
} from "./CalendarUtils";

export default function Row({ className, ...props }: WeeksProps) {
	const [hoveredId, setHoveredId] = useState<number | null>(null);
	const today = useMemo(() => new Date(), []);

	const { selected, setSelected, nurseCount } = useContext(DayPickerContext);
	const dayPicker = useDayPicker();

	const _onChangeDay = useCallback(
		(date: Date) => {
			setSelected?.(date);
		},
		[setSelected],
	);

	/**
	 * Obtenemos qué tantas enfermeras hay asignadas en un día y turno específico.
	 * @param date Fecha de los datos que queremos
	 * @param shiftNumber Donde 0: matutino, 1: vespertino, 2: nocturno
	 * @returns Cantidad de enfermeras
	 */
	const getCounting = useCallback(
		(date: Date, shiftNumber: number): number => {
			const dateString: string = date.getFullYear() + "-" +
              String(date.getMonth() + 1).padStart(2, "0") + "-" +
              String(date.getDate()).padStart(2, "0");
			const dayInfo = nurseCount?.[dateString];
			if (dayInfo === undefined) return 0;

			const shiftInfo = dayInfo.find((day: any) =>
				day.schedule.includes(getShiftNameByNumber(shiftNumber)),
			);
			if (shiftInfo === undefined) return 0;

			return shiftInfo.value;
		},
		[nurseCount],
	);

	const getClassNames = (date: Date): string => {
		let classNames = "rdp-day_button";
		if (compareTwoDate(selected!, date)) {
			classNames += " rdp-day_button--selected";
		}
		if (!compareTwoDate(selected!, date) && compareTwoDate(today, date)) {
			classNames += " rdp-day_button--current";
		}

		return classNames;
	};

	return (
		<tbody className={`${className}`}>
			{dayPicker.months[0].weeks.map((week: CalendarWeek) => {
				const days = week.days;

				return (
					<tr
						key={week.weekNumber}
						className={`rdp-week ${
							isCurrentWeek(week.weekNumber) ? "current-week" : ""
						} ${!canDisplayWeek(week) && "hidden-week"}`}
					>
						{days.map((day: CalendarDay) => {
							const { date, outside } = day;
							return (
								<td
									key={date.toString()}
									className={`rdp-day ${outside && "rdp-outside"}`}
									role="gridcell"
									data-day={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
									data-month={`${date.getFullYear()}-${date.getMonth()}`}
									data-outside={`${outside}`}
								>
									<StyleToolTip
										key={date.getTime().toString()}
										open={hoveredId === date.getTime() && !outside}
										placement="top"
										arrow
										title={
											<CustomTooltip
												morningCounting={getCounting(date, 0)}
												afternoonCounting={getCounting(date, 1)}
												nightCounting={getCounting(date, 2)}
											/>
										}
									>
										<button
											onMouseEnter={() => setHoveredId(date.getTime())}
											onMouseLeave={() => setHoveredId(null)}
											key={date.toString()}
											onClick={outside ? undefined : () => _onChangeDay(date)}
											className={getClassNames(date)}
											type="button"
											tabIndex={-1}
										>
											{date.getDate()}
										</button>
									</StyleToolTip>
								</td>
							);
						})}
					</tr>
				);
			})}
		</tbody>
	);
}
